import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { toast } from 'react-toastify';
import { useUser } from '../contexts/user-context';
import { identifyContact, formatContactForUI } from '../services/contactIdentificationService';
import { hasTwilioAccess } from '../utils/userRole';
import { API_BASE_URL } from '../config';

const TwilioContext = createContext();

export const TwilioProvider = ({ children }) => {
  const { user, usuarioId } = useUser(); // Hook para obtener información del usuario
  const [device, setDevice] = useState(null);
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null); // Llamada entrante principal (para compatibilidad)
  const [incomingCalls, setIncomingCalls] = useState([]); // ✨ NUEVO: Array de todas las llamadas entrantes
  const [activeCall, setActiveCall] = useState(null);
  const [activeCalls, setActiveCalls] = useState([]); // ✨ NUEVO: Array de llamadas activas
  const [isMuted, setIsMuted] = useState(false);
  const [token, setToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showAgentSelectionModal, setShowAgentSelectionModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [consultation, setConsultation] = useState(null);
  const [allAgents, setAllAgents] = useState([]); // 📋 Todos los agentes para lookup de nombres
  const [isInitiatingOutgoingCall, setIsInitiatingOutgoingCall] = useState(false); // ✨ Bandera para llamadas salientes
  const deviceRef = useRef(null);
  const isReadyRef = useRef(false);
  const initializationAttempted = useRef(false);
  const outgoingCallsRef = useRef(new Set()); // ✨ Track de llamadas salientes iniciadas por nosotros
  const isInitiatingOutgoingCallRef = useRef(false); // 🔧 Ref para evitar stale closures
  const tokenRefreshTimerRef = useRef(null); // 🔄 Timer para renovar token antes de que expire
  const incomingCallsRef = useRef([]); // 🔧 Ref para acceso en closures
  const userRef = useRef(null); // 🔧 Ref para user en closures de Device
  const consultationRef = useRef(null);

  const closeConsultationForEndedCall = (call) => {
    const current = consultationRef.current;
    if (!current || current.originalCall !== call) return;
    consultationRef.current = null;
    setConsultation(null);
    current.call?.disconnect();
    current.device?.destroy();
  };

  // 📞 Notifica al backend que este agente está ocupado / disponible
  const notifyCallStarted = useCallback(async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      await fetch(`${API_BASE_URL}/api/twilio/voice/agent/call-started`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      console.log('📞 Backend notificado: agente OCUPADO');
    } catch (e) {
      console.warn('⚠️ Error notificando call-started:', e);
    }
  }, []);

  const notifyCallEnded = useCallback(async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;
      await fetch(`${API_BASE_URL}/api/twilio/voice/agent/call-ended`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      console.log('✅ Backend notificado: agente DISPONIBLE');
    } catch (e) {
      console.warn('⚠️ Error notificando call-ended:', e);
    }
  }, []);
  const ringtoneAudioRef = useRef(null); // 🔔 Audio para ringtone de llamadas entrantes
  const audioContextActivatedRef = useRef(false); // 🔊 Track si AudioContext fue activado
  
  // 🚫 Verificar si el usuario tiene acceso a Twilio
  const userHasTwilioAccess = hasTwilioAccess();

  const sendCallDiagnostic = useCallback((eventType, call, details = {}) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const payload = {
      eventType,
      callSid: call?.parameters?.CallSid || call?.sid || null,
      details
    };
    fetch(`${API_BASE_URL}/api/twilio/diagnostics`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(error => console.warn('No se pudo enviar diagnóstico de llamada:', error));
  }, []);

  // 🔧 Mantener refs sincronizados con state para evitar stale closures
  useEffect(() => {
    isInitiatingOutgoingCallRef.current = isInitiatingOutgoingCall;
  }, [isInitiatingOutgoingCall]);
  
  useEffect(() => {
    incomingCallsRef.current = incomingCalls;
  }, [incomingCalls]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  // 🔊 Activar AudioContext con el primer click del usuario
  useEffect(() => {
    const activateAudioContext = async () => {
      if (audioContextActivatedRef.current || !deviceRef.current) return;
      
      try {
        const audioContext = deviceRef.current.audio?._audioContext || deviceRef.current.audio?.context;
        if (audioContext && audioContext.state === 'suspended') {
          console.log('🔊 Usuario hizo click - Activando AudioContext...');
          await audioContext.resume();
          audioContextActivatedRef.current = true;
          console.log('✅ AudioContext activado por interacción del usuario:', audioContext.state);
        }
      } catch (error) {
        console.warn('⚠️ Error activando AudioContext:', error);
      }
    };
    
    // Agregar listener para primer click
    document.addEventListener('click', activateAudioContext, { once: false });
    
    return () => {
      document.removeEventListener('click', activateAudioContext);
    };
  }, [device]);
  
  // 🔔 Inicializar audio de ringtone
  useEffect(() => {
    // Crear instancia de Audio para el ringtone
    ringtoneAudioRef.current = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-classic-phone-ringtone-1356.mp3');
    ringtoneAudioRef.current.loop = true; // Loop hasta que se acepte o rechace
    ringtoneAudioRef.current.volume = 0.5; // Volumen al 50%
    
    return () => {
      // Limpiar al desmontar
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current = null;
      }
    };
  }, []);

  // Función para obtener el token del backend
  const getAccessToken = useCallback(async (identity) => {
    try {
      console.log('Solicitando token para identity:', identity);
      
      // Obtener el token de autenticación del localStorage
      const authToken = localStorage.getItem('authToken');
      console.log('Token de autenticación encontrado:', !!authToken);
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Agregar el token de autenticación si existe
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/twilio/token?identity=${identity}`, {
        method: 'GET',
        headers: headers
      });
      
      console.log('Respuesta del servidor:', response.status, response.statusText);
      
      if (response.ok) {
        const tokenData = await response.text();
        console.log('Token recibido:', tokenData ? 'Token válido' : 'Token vacío');
        console.log('🟢 IMPORTANTE: Al obtener el token, el backend debería haber registrado la conexión del agente:', identity);
        setToken(tokenData);
        return tokenData;
      } else {
        const errorText = await response.text();
        console.error('Error del servidor:', response.status, errorText);
        throw new Error(`Error obteniendo el token: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error al obtener el token de Twilio:', error);
      toast.error(`Error al conectar con el servicio de llamadas: ${error.message}`);
      return null;
    }
  }, []);

  // Inicializar el dispositivo Twilio
  const initializeDevice = useCallback(async (twilioToken) => {
    console.log('🔧 INITIALIZE DEVICE: Iniciando...');
    console.log('🔑 Token length:', twilioToken ? twilioToken.length : 'No token');
    console.log('🔑 Token preview:', twilioToken ? twilioToken.substring(0, 50) + '...' : 'No token');
    
    try {
      if (deviceRef.current) {
        console.log('🗑️ Destruyendo dispositivo anterior...');
        deviceRef.current.destroy();
      }

      console.log('🆕 Creando nuevo dispositivo Twilio...');
      const newDevice = new Device(twilioToken, { 
        debug: process.env.NODE_ENV === 'development'
      });
      
      console.log('💾 Guardando referencia del dispositivo...');
      deviceRef.current = newDevice;
      
      console.log('🎧 Configurando listeners...');

      // Listener para cuando el dispositivo está listo
      newDevice.on('ready', () => {
        console.log('🎉 EVENTO READY: Twilio Device está listo');
        isReadyRef.current = true;
        setIsDeviceReady(true);
        setDevice(newDevice);
      });

      // Listener para registrar todos los estados del dispositivo
      newDevice.on('stateChanged', (state) => {
        console.log('📱 DEVICE STATE CHANGED:', state);
        if (state === 'ready') {
          console.log('✅ Device state is READY');
          setIsDeviceReady(true);
        }
        // 🔥 FIX: Si el dispositivo está 'registered', considerarlo listo también
        // Algunos dispositivos nunca disparan 'ready' pero sí pueden recibir llamadas en 'registered'
        if (state === 'registered' && !isReadyRef.current) {
          console.log('✅ Device state is REGISTERED - Activando como ready');
          isReadyRef.current = true;
          setIsDeviceReady(true);
          setDevice(newDevice);
        }
      });

      // Listener para cuando el dispositivo se registra
      newDevice.on('registered', async () => {
        console.log('📝 DEVICE REGISTERED');
        
        // 🔊 CRITICAL FIX: Activar AudioContext para permitir audio entrante
        try {
          // Obtener el audio context interno de Twilio
          const audioContext = newDevice.audio?._audioContext || newDevice.audio?.context;
          if (audioContext && audioContext.state === 'suspended') {
            console.log('🔊 Intentando activar AudioContext...');
            await audioContext.resume();
            console.log('✅ AudioContext activado:', audioContext.state);
          }
        } catch (error) {
          console.warn('⚠️ No se pudo activar AudioContext automáticamente:', error);
          console.log('💡 El usuario debe hacer click en la página para activar el audio');
        }
      });

      // Listener para cuando el dispositivo se desregistra
      newDevice.on('unregistered', () => {
        console.log('📝 DEVICE UNREGISTERED');
        setIsDeviceReady(false);
      });

      // 🔄 CRITICAL FIX: Renovar token ANTES de que expire
      // Twilio SDK v2 dispara 'tokenWillExpire' ~10s antes de expiración
      newDevice.on('tokenWillExpire', async () => {
        console.log('⏰ TOKEN A PUNTO DE EXPIRAR - Renovando...');
        try {
          // 🔧 FIX: Usar userRef para evitar stale closure
          const currentUser = userRef.current;
          const identity = currentUser?.username ? currentUser.username : `user_${currentUser?.usuarioId}`;
          const newToken = await getAccessToken(identity);
          if (newToken && deviceRef.current) {
            deviceRef.current.updateToken(newToken);
            console.log('✅ Token renovado exitosamente - dispositivo sigue activo');
          } else {
            console.error('❌ No se pudo renovar el token - el dispositivo podría desconectarse');
          }
        } catch (error) {
          console.error('❌ Error renovando token:', error);
        }
      });

      // Listener para llamadas entrantes
      newDevice.on('incoming', (call) => {
        console.log('🚨🚨🚨 EVENTO INCOMING DISPARADO 🚨🚨🚨');
        console.log('📞 Call object:', call);
        console.log('📞 Call parameters:', call.parameters);
        console.log('📞 Call customParameters:', call.customParameters);
        
        const callId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // 🔍 EXTRAER EL NÚMERO REAL (no el client:xxx)
        // Para llamadas entrantes, el número real puede estar en diferentes lugares:
        // - call.parameters.From (puede ser client:xxx si viene de otro agente)
        // - call.customParameters.get('caller_number') (si lo configuramos en TwiML)
        // - Necesitamos buscar el número telefónico real
        
        const rawFrom = call.parameters.From || 'Número desconocido';
        let fromNumber = rawFrom;
        
        // Si el From es un client:xxx, intentar obtener el número real de los parámetros personalizados
        if (rawFrom.includes('client:')) {
          console.log('⚠️ Llamada de cliente Twilio detectada:', rawFrom);
          // Buscar en parámetros personalizados
          const customParams = call.customParameters;
          console.log('📋 Parámetros personalizados:', customParams);
          
          // Intentar obtener el número original
          if (customParams && customParams.get) {
            const originalCaller = customParams.get('caller_number') || 
                                  customParams.get('original_caller') ||
                                  customParams.get('From');
            if (originalCaller && !originalCaller.includes('client:')) {
              fromNumber = originalCaller;
              console.log('✅ Número real encontrado en parámetros personalizados:', fromNumber);
            }
          }
          
          // Si aún no tenemos número real, buscar en todos los parámetros
          if (fromNumber.includes('client:')) {
            console.log('🔍 Buscando número real en todos los parámetros...');
            console.log('📋 Todos los parámetros:', call.parameters);
            
            // Revisar si hay un parámetro que contenga un número telefónico
            for (const [key, value] of Object.entries(call.parameters)) {
              if (value && typeof value === 'string' && 
                  value.startsWith('+') && 
                  !value.includes('client:')) {
                fromNumber = value;
                console.log(`✅ Número real encontrado en parámetro "${key}":`, fromNumber);
                break;
              }
            }
          }
        }
        
        console.log('🔔 Nueva llamada entrante de:', fromNumber, '(raw:', rawFrom, ')');
        console.log('📋 Parámetros completos de la llamada:', call.parameters);
        console.log('🔍 Es una transferencia?', call.parameters.hasOwnProperty('Forwarded') || call.parameters.hasOwnProperty('Transfer'));
        
        // ✨ VERIFICAR: ¿Estamos iniciando una llamada saliente?
        // 🔧 FIX: Usar ref en lugar de state para evitar stale closures
        if (isInitiatingOutgoingCallRef.current) {
          console.log('🚫 Ignorando evento incoming - estamos iniciando llamada saliente');
          return; // No procesar como llamada entrante
        }
        
        // También verificar por CallSid
        const callSid = call.parameters.CallSid || call.sid;
        if (outgoingCallsRef.current.has(callSid)) {
          console.log('🚫 Ignorando evento incoming para llamada saliente registrada:', callSid);
          return; // No procesar como llamada entrante
        }
        
        const callInfo = {
          id: callId,
          call: call,
          from: fromNumber,
          contactInfo: {
            name: fromNumber,
            phone: fromNumber,
            isKnown: false
          },
          timestamp: new Date(),
          status: 'incoming'
        };

        console.log('LLAMADA ENTRANTE - Mostrando alerta sin esperar la identificación:', callInfo);
        sendCallDiagnostic('incoming', call, { fromType: rawFrom.startsWith('client:') ? 'client' : 'phone' });
        
        // REPRODUCIR RINGTONE al recibir llamada entrante
        if (ringtoneAudioRef.current) {
          ringtoneAudioRef.current.play().catch(err => {
            console.error('Error reproduciendo ringtone:', err);
          });
        }
        
        // ✨ Agregar a la lista de llamadas entrantes
        setIncomingCalls(prev => [...prev, callInfo]);
        
        // Mantener compatibilidad: si es la primera llamada, también setearla como incomingCall
        setIncomingCall(prevCall => prevCall || call);

        // La identificación de contacto es secundaria: no debe retrasar el timbre ni el modal.
        const isRealNumber = !fromNumber.includes('client:') &&
          !fromNumber.toLowerCase().includes('anonymous') &&
          !fromNumber.toLowerCase().includes('unknown');
        if (isRealNumber) {
          identifyContact(fromNumber)
            .then(rawContactInfo => {
              const identified = formatContactForUI(rawContactInfo) || {
                name: fromNumber,
                phone: fromNumber,
                isKnown: false
              };
              if (!identified.phone) identified.phone = fromNumber;
              setIncomingCalls(prev => prev.map(item =>
                item.id === callId ? { ...item, contactInfo: identified } : item
              ));
            })
            .catch(error => console.warn('No se pudo identificar contacto entrante:', error));
        }
        
        // Configurar listeners para esta llamada específica
        call.on('accept', () => {
          console.log('✅ Llamada aceptada:', callId);
          sendCallDiagnostic('accepted', call);
          notifyCallStarted(); // 📞 Marcar agente como OCUPADO
          
          // 🔇 DETENER RINGTONE al aceptar
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
          }
          
          // Mover de incoming a active
          setIncomingCalls(prev => prev.filter(c => c.id !== callId));
          setActiveCalls(prev => [...prev, { ...callInfo, status: 'active' }]);
          
          // Mantener compatibilidad con activeCall (primera llamada activa)
          setActiveCall(prevActive => prevActive || call);
          
          // Si esta era la llamada principal, limpiar incomingCall
          setIncomingCall(prevCall => prevCall === call ? null : prevCall);
          
          // Reset mute state para nueva llamada
          setIsMuted(false);
        });

        call.on('disconnect', () => {
          console.log('📞 Llamada desconectada:', callId);
          closeConsultationForEndedCall(call);
          sendCallDiagnostic('disconnected', call, { duration: call.duration || null });
          
          // 🔧 FIX: Solo notificar call-ended si la llamada estaba activa (no solo sonando)
          // Verificar si estaba en la lista de llamadas activas antes de notificar
          const wasActive = call.status && call.status() !== 'pending';
          if (wasActive) {
            notifyCallEnded(); // ✅ Marcar agente como DISPONIBLE
          }
          
          // 🔇 DETENER RINGTONE también en disconnect (por si cancel no se disparó)
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
          }
          
          // Remover de ambas listas
          setIncomingCalls(prev => prev.filter(c => c.id !== callId));
          setActiveCalls(prev => prev.filter(c => c.id !== callId));
          
          // Actualizar estados de compatibilidad
          setIncomingCall(prevCall => prevCall === call ? null : prevCall);
          setActiveCall(prevActive => prevActive === call ? null : prevActive);
          
          // Reset mute state cuando se termina la llamada
          setIsMuted(false);
        });

        call.on('reject', () => {
          console.log('❌ Llamada rechazada:', callId);
          
          // 🔇 DETENER RINGTONE al rechazar
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
          }
          
          // Remover de incoming calls
          setIncomingCalls(prev => prev.filter(c => c.id !== callId));
          
          // Si esta era la llamada principal, limpiar incomingCall
          setIncomingCall(prevCall => prevCall === call ? null : prevCall);
        });

        // ✨ Evento cuando el llamante cuelga antes de que respondamos
        call.on('cancel', () => {
          console.log('📞 Llamada cancelada por el llamante:', callId);
          sendCallDiagnostic('cancelled', call);
          
          // 🔇 DETENER RINGTONE si el llamante cancela
          if (ringtoneAudioRef.current) {
            ringtoneAudioRef.current.pause();
            ringtoneAudioRef.current.currentTime = 0;
          }
          
          // Remover de incoming calls
          setIncomingCalls(prev => prev.filter(c => c.id !== callId));
          
          // Si esta era la llamada principal, limpiar incomingCall
          setIncomingCall(prevCall => prevCall === call ? null : prevCall);
          
          // Reset mute state
          setIsMuted(false);
        });

        // 🔧 SAFETY NET: Auto-dismiss llamadas entrantes que se quedan pilladas
        // Si después de 35s (>timeout de Twilio de 30s) la llamada sigue como incoming,
        // es que el evento cancel/disconnect no llegó → limpiar manualmente
        const safetyTimeout = setTimeout(() => {
          const stillIncoming = incomingCallsRef.current.some(c => c.id === callId);
          if (stillIncoming) {
            console.warn('⏰ SAFETY: Llamada', callId, 'lleva >35s sonando - auto-dismissing');
            
            // Detener ringtone
            if (ringtoneAudioRef.current) {
              ringtoneAudioRef.current.pause();
              ringtoneAudioRef.current.currentTime = 0;
            }
            
            // Limpiar de la lista
            setIncomingCalls(prev => prev.filter(c => c.id !== callId));
            setIncomingCall(prevCall => prevCall === call ? null : prevCall);
          }
        }, 35000); // 35 segundos

        // Limpiar el safety timeout si la llamada se resuelve normalmente
        call.on('accept', () => clearTimeout(safetyTimeout));
        call.on('disconnect', () => clearTimeout(safetyTimeout));
        call.on('reject', () => clearTimeout(safetyTimeout));
        call.on('cancel', () => clearTimeout(safetyTimeout));

        call.on('sample', sample => {
          if (!sample) return;
          const quality = {};
          ['mos', 'rtt', 'jitter', 'packetsLostFraction', 'packetsLost', 'packetsReceived', 'audioInputLevel', 'audioOutputLevel'].forEach(key => {
            if (typeof sample[key] === 'number' && Number.isFinite(sample[key])) quality[key] = sample[key];
          });
          if (Object.keys(quality).length) sendCallDiagnostic('quality_sample', call, quality);
        });
        call.on('warning', (name, data) => {
          sendCallDiagnostic('warning', call, { warning: String(name).slice(0, 80), data: data || {} });
        });
        call.on('warning-cleared', name => {
          sendCallDiagnostic('warning_cleared', call, { warning: String(name).slice(0, 80) });
        });
        call.on('reconnecting', error => {
          sendCallDiagnostic('reconnecting', call, { code: error?.code || null });
        });
        call.on('reconnected', () => sendCallDiagnostic('reconnected', call));
      });

      // Listener adicional para el estado destroy
      newDevice.on('destroy', () => {
        console.log('🗑️ DEVICE DESTROYED');
        setIsDeviceReady(false);
        setDevice(null);
      });

      // Listener para errores
      newDevice.on('error', (error) => {
        console.error('❌ ERROR en Twilio Device:', error);
        sendCallDiagnostic('device_error', null, { code: error.code || null, message: String(error.message || '').slice(0, 240) });
        toast.error('Error en el servicio de llamadas');
        setIsDeviceReady(false);
      });

      console.log('✅ DEVICE SETUP: Listeners configurados, esperando evento ready...');
      console.log('🔍 Device state actual:', newDevice.state);
      
      // Si el dispositivo ya está listo (edge case)
      if (newDevice.state === 'ready') {
        console.log('⚡ Device ya estaba ready, activando inmediatamente');
        setIsDeviceReady(true);
        setDevice(newDevice);
      }
      
      // Forzar registro del dispositivo si está unregistered
      if (newDevice.state === 'unregistered') {
        console.log('🔄 Dispositivo unregistered, forzando registro...');
        try {
          await newDevice.register();
          console.log('✅ Registro forzado completado');
          
          // 🔥 FIX: Después de registrar, verificar si ya está listo
          setTimeout(() => {
            if (newDevice.state === 'registered' && !isReadyRef.current) {
              console.log('✅ Dispositivo registered después de forzar registro - Activando como ready');
              isReadyRef.current = true;
              setIsDeviceReady(true);
              setDevice(newDevice);
            }
          }, 1000); // Esperar 1 segundo después del registro
          
        } catch (registerError) {
          console.error('❌ Error al forzar registro:', registerError);
          // Intentar activar como ready de todas formas si no hay error crítico
          console.log('🔧 Intentando activar dispositivo sin registro...');
          setIsDeviceReady(true);
          setDevice(newDevice);
        }
      }

      // Timeout de seguridad - forzar ready después de 5 segundos si no ocurre
      setTimeout(() => {
        if (!isReadyRef.current && newDevice) {
          console.log('⏰ TIMEOUT: Forzando device ready después de 5 segundos');
          console.log('🔍 Estado actual del dispositivo:', newDevice.state);
          
          // 🔥 FIX: Si está en 'registered', activarlo como ready
          if (newDevice.state === 'registered' || newDevice.state === 'ready') {
            console.log('✅ Dispositivo en estado válido (' + newDevice.state + ') - Activando');
            isReadyRef.current = true;
            setIsDeviceReady(true);
            setDevice(newDevice);
          }
        }
      }, 5000);

      return newDevice;
    } catch (error) {
      console.error('❌ Error inicializando el dispositivo Twilio:', error);
      toast.error('Error al inicializar el servicio de llamadas');
      setIsDeviceReady(false);
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessToken, notifyCallStarted, notifyCallEnded, sendCallDiagnostic]);

  // Realizar una llamada saliente
  const makeCall = async (to) => {
    console.log('=== DEBUG MAKE CALL ===');
    console.log('📞 Parámetros de llamada:', { to });
    console.log('🔧 Estado del dispositivo:', {
      device: !!device,
      isDeviceReady,
      token: !!token
    });
    
    try {
      if (isInitiatingOutgoingCallRef.current) {
        toast.info('Ya se está iniciando una llamada');
        return;
      }
      if (!device || !isDeviceReady) {
        console.error('❌ Dispositivo no listo:', {
          device: !!device,
          isDeviceReady
        });
        toast.error('El servicio de llamadas no está listo');
        return;
      }

      console.log('✅ Dispositivo listo, iniciando llamada con device.connect()...');
      
      // ✨ ACTIVAR bandera para evitar procesar como incoming
      isInitiatingOutgoingCallRef.current = true;
      setIsInitiatingOutgoingCall(true);
      
      // Parámetros para la llamada saliente
      const connectOptions = {
        params: {
          To: to
        }
      };
      
      console.log('📋 Opciones de conexión:', connectOptions);
      
      // Usar el dispositivo Twilio para iniciar la llamada directamente
      const call = await device.connect(connectOptions);
      sendCallDiagnostic('outgoing_started', call, { destinationType: String(to).startsWith('client:') || !String(to).startsWith('+') ? 'agent' : 'phone' });
      
      // ✨ Preservar información de la llamada saliente
      call._isOutgoing = true;
      call._destinationNumber = to;
      call._callType = 'outgoing';
      
      // ✨ DESACTIVAR bandera después de iniciar (aumentamos el tiempo)
      setTimeout(() => {
        isInitiatingOutgoingCallRef.current = false;
        setIsInitiatingOutgoingCall(false);
        console.log('🔄 Bandera de llamada saliente desactivada');
      }, 5000); // 5 segundos de buffer
      
      console.log('✅ Llamada iniciada exitosamente:', {
        callSid: call.parameters?.CallSid,
        to: call.parameters?.To,
        from: call.parameters?.From,
        destinationNumber: call._destinationNumber,
        isOutgoing: call._isOutgoing,
        status: call.status,
        allParameters: call.parameters
      });
      console.log('🔍 Objeto call completo:', call);
      
      // ✨ REGISTRAR esta llamada como saliente para evitar duplicados
      const callSid = call.parameters?.CallSid || call.sid;
      if (callSid) {
        outgoingCallsRef.current.add(callSid);
        console.log('📝 Registrada llamada saliente:', callSid);
      }
      
      // Configurar listeners para esta llamada saliente
      const callId = `outgoing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const callInfo = {
        id: callId,
        call: call,
        to: to,
        timestamp: new Date(),
        status: 'outgoing'
      };
      
      // ✨ Agregar el número de destino al objeto call para fácil acceso
      call._destinationNumber = to;
      
      // Agregar a llamadas activas inmediatamente
      setActiveCalls(prev => [...prev, { ...callInfo, status: 'active' }]);
      setActiveCall(call);
      
      // Configurar listeners específicos para esta llamada
      call.on('accept', () => {
        console.log('✅ Llamada saliente aceptada (conectada):', callId);
        sendCallDiagnostic('accepted', call);
        notifyCallStarted(); // 📞 Marcar agente como OCUPADO
        // Actualizar estado a conectado
        setActiveCalls(prev => 
          prev.map(c => c.id === callId ? { ...c, status: 'connected' } : c)
        );
      });

      // ✨ NUEVO: Listener para cuando la llamada realmente se establece (audio conectado)
      call.on('connect', () => {
        console.log('🔗 Llamada saliente conectada (audio activo):', callId);
        notifyCallStarted(); // 📞 Marcar agente como OCUPADO (por si 'accept' no se dispara)
        // Actualizar estado a conectado
        setActiveCalls(prev => 
          prev.map(c => c.id === callId ? { ...c, status: 'connected' } : c)
        );
      });

      // ✨ NUEVO: Listener para cambios de estado
      call.on('stateChanged', (state) => {
        console.log('🔄 Estado de llamada saliente cambió:', callId, 'Estado:', state);
      });

      call.on('sample', sample => {
        if (!sample) return;
        const quality = {};
        ['mos', 'rtt', 'jitter', 'packetsLostFraction', 'packetsLost', 'packetsReceived', 'audioInputLevel', 'audioOutputLevel'].forEach(key => {
          if (typeof sample[key] === 'number' && Number.isFinite(sample[key])) quality[key] = sample[key];
        });
        if (Object.keys(quality).length) sendCallDiagnostic('quality_sample', call, quality);
      });
      call.on('warning', (name, data) => {
        sendCallDiagnostic('warning', call, { warning: String(name).slice(0, 80), data: data || {} });
      });
      call.on('reconnecting', error => {
        sendCallDiagnostic('reconnecting', call, { code: error?.code || null });
      });
      call.on('reconnected', () => sendCallDiagnostic('reconnected', call));

      call.on('disconnect', () => {
        console.log('📞 Llamada saliente desconectada:', callId);
        closeConsultationForEndedCall(call);
        sendCallDiagnostic('disconnected', call, { duration: call.duration || null });
        notifyCallEnded(); // ✅ Marcar agente como DISPONIBLE
        
        // ✨ LIMPIAR el registro de llamada saliente
        const callSid = call.parameters?.CallSid || call.sid;
        if (callSid) {
          outgoingCallsRef.current.delete(callSid);
          console.log('🗑️ Limpiado registro de llamada saliente:', callSid);
        }
        
        setActiveCalls(prev => prev.filter(c => c.id !== callId));
        setActiveCall(prevActive => prevActive === call ? null : prevActive);
        setIsMuted(false);
      });

      call.on('reject', () => {
        console.log('❌ Llamada saliente rechazada:', callId);
        
        // ✨ LIMPIAR el registro de llamada saliente
        const callSid = call.parameters?.CallSid || call.sid;
        if (callSid) {
          outgoingCallsRef.current.delete(callSid);
          console.log('🗑️ Limpiado registro de llamada saliente:', callSid);
        }
        
        setActiveCalls(prev => prev.filter(c => c.id !== callId));
        setActiveCall(prevActive => prevActive === call ? null : prevActive);
        setIsMuted(false);
      });

      return call;
      
    } catch (error) {
      isInitiatingOutgoingCallRef.current = false;
      setIsInitiatingOutgoingCall(false);
      console.error('❌ Error en makeCall:', {
        message: error.message,
        stack: error.stack
      });
      toast.error(`Error al realizar la llamada: ${error.message}`);
      throw error;
    } finally {
      console.log('=== FIN DEBUG MAKE CALL ===');
    }
  };

  // Aceptar llamada entrante (compatibilidad)
  const acceptCall = () => {
    if (incomingCall) {
      incomingCall.accept();
    }
  };

  // ✨ Aceptar llamada específica por ID
  const acceptCallById = (callId) => {
    const callInfo = incomingCalls.find(c => c.id === callId);
    if (callInfo && callInfo.call) {
      callInfo.call.accept();
    }
  };

  // Rechazar llamada entrante (compatibilidad)
  const rejectCall = () => {
    if (incomingCall) {
      incomingCall.reject();
    }
  };

  // ✨ Rechazar llamada específica por ID
  const rejectCallById = (callId) => {
    const callInfo = incomingCalls.find(c => c.id === callId);
    if (callInfo && callInfo.call) {
      callInfo.call.reject();
    }
  };

  // Colgar llamada activa (compatibilidad)
  const hangUp = () => {
    if (activeCall) {
      activeCall.disconnect();
    }
  };

  // ✨ Colgar llamada específica por ID
  const hangUpById = (callId) => {
    const callInfo = activeCalls.find(c => c.id === callId);
    if (callInfo && callInfo.call) {
      callInfo.call.disconnect();
    }
  };

  // ✨ Colgar todas las llamadas
  const hangUpAll = () => {
    activeCalls.forEach(callInfo => {
      if (callInfo.call) {
        callInfo.call.disconnect();
      }
    });
  };

  // Silenciar/Desilenciar llamada
  const toggleMute = () => {
    console.log('🔧 toggleMute llamado - Estado actual:', { activeCall: !!activeCall, isMuted });
    
    if (activeCall) {
      try {
        // Verificar qué métodos están disponibles en activeCall
        console.log('🔍 Métodos disponibles en activeCall:', Object.getOwnPropertyNames(activeCall));
        console.log('🔍 Prototipo de activeCall:', Object.getOwnPropertyNames(Object.getPrototypeOf(activeCall)));
        
        if (typeof activeCall.mute === 'function') {
          if (isMuted) {
            // Unmute - el método correcto es mute(false)
            activeCall.mute(false);
            setIsMuted(false);
            console.log('🎤 Micrófono activado');
          } else {
            // Mute - el método correcto es mute(true)
            activeCall.mute(true);
            setIsMuted(true);
            console.log('🔇 Micrófono silenciado');
          }
        } else {
          console.error('❌ El método mute no está disponible en activeCall');
          toast.error('Función de mute no disponible');
        }
      } catch (error) {
        console.error('❌ Error al cambiar estado del micrófono:', error);
        toast.error('Error al cambiar estado del micrófono');
      }
    } else {
      console.warn('⚠️ No hay llamada activa');
      toast.warning('No hay llamada activa para silenciar');
    }
  };

  // ✨ Transferir llamada a otro agente
  const transferCall = async () => {
    if (!activeCall) {
      toast.warning('No hay llamada activa para transferir');
      return;
    }

    try {
      console.log('🔍 DEBUG - Usuario actual:', user);
      console.log('🔍 DEBUG - user.id:', user?.id);
      console.log('🔍 DEBUG - usuarioId del contexto:', usuarioId);
      console.log('🔍 DEBUG - Todas las propiedades de user:', Object.keys(user || {}));
      
      // Obtener TODOS los agentes (disponibles + ocupados) para mostrar estado real
      const todosLosAgentes = await obtenerTodosLosAgentes();
      console.log('🔍 DEBUG - Todos los agentes:', todosLosAgentes);
      
      // Filtrar para excluir al usuario actual
      const agentesFiltered = todosLosAgentes.filter(agente => {
        const shouldInclude = usuarioId && agente.id !== parseInt(usuarioId) &&
          agente.conectado !== false && agente.disponible !== false && agente.estado !== 'en_llamada';
        console.log(`🔍 Agente ${agente.nombre} (id: ${agente.id}) - estado: ${agente.estado} - incluir: ${shouldInclude}`);
        return shouldInclude;
      });
      console.log('🔍 DEBUG - Agentes después de filtrar:', agentesFiltered);
      
      if (agentesFiltered.length === 0) {
        toast.warning('No hay agentes disponibles para transferir');
        return;
      }

      // Mostrar modal de selección de agente
      setAvailableAgents(agentesFiltered);
      setShowAgentSelectionModal(true);
      
    } catch (error) {
      console.error('❌ Error al obtener agentes disponibles:', error);
      toast.error('Error al obtener lista de agentes');
    }
  };

  // Función para verificar estadísticas de agentes conectados
  const checkAgentStats = async () => {
    console.log('📊 VERIFICANDO ESTADÍSTICAS DE AGENTES...');
    
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/twilio/stats`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const stats = await response.json();
        console.log('📈 Estadísticas del backend:', stats);
        return stats;
      } else {
        console.error('❌ Error obteniendo estadísticas:', response.status);
        return null;
      }
    } catch (error) {
      console.error('❌ Error al verificar estadísticas:', error);
      return null;
    }
  };

  // Función para verificar el estado específico de un agente
  const verificarEstadoAgente = async (agenteId) => {
    console.log('🔍 VERIFICANDO ESTADO DE AGENTE:', agenteId);
    
    try {
      // 🔧 FIX: Usar obtenerTodosLosAgentes para ver el estado real (incluyendo "en_llamada")
      const todosLosAgentes = await obtenerTodosLosAgentes();
      
      console.log('👥 Todos los agentes:', todosLosAgentes);
      
      const agente = todosLosAgentes.find(a => a.id === agenteId);
      if (agente) {
        const conectado = agente.conectado !== false;
        console.log('✅ Agente encontrado:', agente.nombre, '- estado:', agente.estado, '- conectado:', conectado);
        return { conectado, agente };
      } else {
        console.log('❌ Agente no encontrado');
        return { conectado: false, agente: null };
      }
    } catch (error) {
      console.error('❌ Error verificando estado de agente:', error);
      return { conectado: false, agente: null, error };
    }
  };

  // Obtener agentes disponibles desde el backend
  const obtenerAgentesDisponibles = async () => {
    console.log('🔍 OBTENIENDO AGENTES DISPONIBLES...');
    
    try {
      const authToken = localStorage.getItem('authToken');
      console.log('🔑 Auth token presente:', !!authToken);
      
      const url = `${API_BASE_URL}/api/twilio/agents/available`;
      console.log('🌐 URL del endpoint:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Respuesta del servidor:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Agentes obtenidos del backend:', data);
        console.log('📊 Número de agentes:', data.length);
        
        if (data.length === 0) {
          console.warn('⚠️ No hay agentes conectados actualmente');
        }
        
        return data;
      } else {
        const errorText = await response.text();
        console.error('❌ Error del servidor:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error obteniendo agentes:', error);
      return [];
    }
  };

  // 📋 Obtener TODOS los agentes (para lookup de nombres)
  const obtenerTodosLosAgentes = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return [];
      
      const response = await fetch(`${API_BASE_URL}/api/twilio/agents/all`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📋 Todos los agentes cargados:', data.length);
        return data;
      }
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo todos los agentes:', error);
      return [];
    }
  };

  // 📋 Cargar todos los agentes al inicio (para mostrar nombres)
  useEffect(() => {
    if (isDeviceReady && allAgents.length === 0) {
      obtenerTodosLosAgentes().then(agents => {
        setAllAgents(agents);
        console.log('✅ Agentes cargados para lookup de nombres:', agents.length);
      });
    }
  }, [isDeviceReady]);

  const restoreConsultationAudio = (current) => {
    try {
      current.originalCall?.mute?.(current.originalMuted);
      current.remoteTrackStates.forEach(({ track, enabled }) => { track.enabled = enabled; });
      setIsMuted(current.originalMuted);
    } catch (error) {
      console.warn('No se pudo restaurar el audio de la llamada original:', error);
    }
  };

  const clearConsultation = ({ restoreAudio = true, disconnectCall = true } = {}) => {
    const current = consultationRef.current;
    if (!current) return;

    consultationRef.current = null;
    setConsultation(null);
    if (restoreAudio) restoreConsultationAudio(current);
    if (disconnectCall && current.call && current.call.status?.() !== 'closed') {
      current.call.disconnect();
    }
    current.device?.destroy();
  };

  const startWarmConsultation = async (agent) => {
    if (!activeCall || consultationRef.current) return;

    const originalCallSid = activeCall.parameters?.CallSid || activeCall.sid;
    if (!originalCallSid) {
      toast.error('Twilio aún no ha asignado un identificador a esta llamada');
      return;
    }

    const username = agent.username || agent.email?.split('@')[0];
    if (!username || !token) {
      toast.error('No se pudo iniciar la consulta con este agente');
      return;
    }

    const originalMuted = typeof activeCall.isMuted === 'function' ? activeCall.isMuted() : isMuted;
    const remoteTrackStates = [];
    try {
      activeCall.getRemoteStream?.()?.getAudioTracks?.().forEach(track => {
        remoteTrackStates.push({ track, enabled: track.enabled });
        track.enabled = false;
      });
      activeCall.mute(true);
      setIsMuted(true);
    } catch (error) {
      remoteTrackStates.forEach(({ track, enabled }) => { track.enabled = enabled; });
      toast.error('No se pudo poner al cliente en espera de forma segura');
      return;
    }

    let consultationDevice;
    try {
      consultationDevice = new Device(token, {
        debug: process.env.NODE_ENV === 'development'
      });
    } catch (error) {
      activeCall.mute(originalMuted);
      remoteTrackStates.forEach(({ track, enabled }) => { track.enabled = enabled; });
      setIsMuted(originalMuted);
      toast.error(`No se pudo iniciar la consulta: ${error.message || 'dispositivo no disponible'}`);
      return;
    }
    const current = {
      agent,
      originalCall: activeCall,
      originalCallSid,
      originalMuted,
      remoteTrackStates,
      device: consultationDevice,
      call: null,
      callSid: null,
      phase: 'calling',
      completing: false
    };
    consultationRef.current = current;
    setConsultation({ agentName: agent.nombre, phase: 'calling' });
    setShowAgentSelectionModal(false);
    setAvailableAgents([]);

    try {
      const consultCall = await consultationDevice.connect({ params: { To: username } });
      current.call = consultCall;
      consultCall.on('ringing', () => {
        if (consultationRef.current !== current) return;
        current.phase = 'ringing';
        setConsultation({ agentName: agent.nombre, phase: 'ringing' });
      });
      consultCall.on('accept', () => {
        if (consultationRef.current !== current) return;
        current.phase = 'connected';
        current.callSid = consultCall.parameters?.CallSid || consultCall.sid || null;
        setConsultation({ agentName: agent.nombre, phase: 'connected' });
        toast.success(`Consulta conectada con ${agent.nombre}`);
      });

      const handleConsultationEnded = () => {
        if (consultationRef.current !== current || current.completing) return;
        clearConsultation();
        toast.info(`La consulta con ${agent.nombre} terminó; la llamada con el cliente sigue activa`);
      };
      consultCall.on('disconnect', handleConsultationEnded);
      consultCall.on('reject', handleConsultationEnded);
      consultCall.on('error', (error) => {
        if (consultationRef.current !== current || current.completing) return;
        clearConsultation();
        toast.error(`No se pudo completar la consulta con ${agent.nombre}: ${error.message || 'error de llamada'}`);
      });
    } catch (error) {
      clearConsultation();
      toast.error(`No se pudo llamar a ${agent.nombre}: ${error.message || 'error de conexión'}`);
    }
  };

  const completeWarmTransfer = async () => {
    const current = consultationRef.current;
    if (!current || current.phase !== 'connected' || !current.callSid) {
      toast.warning('La consulta todavía no está conectada');
      return;
    }

    current.completing = true;
    current.phase = 'completing';
    setConsultation({ agentName: current.agent.nombre, phase: 'completing' });

    try {
      await new Promise(resolve => {
        const finish = () => resolve();
        current.call.once('disconnect', finish);
        current.call.disconnect();
        setTimeout(finish, 1500);
      });

      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/twilio/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          callSid: current.originalCallSid,
          targetAgentId: current.agent.id,
          warmTransfer: true,
          consultationCallSid: current.callSid
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'No se pudo transferir la llamada');
      }

      consultationRef.current = null;
      setConsultation(null);
      current.device.destroy();
      toast.success(`Llamada transferida a ${current.agent.nombre}`);
      setTimeout(() => {
        setActiveCalls(prev => prev.filter(callInfo => callInfo.call !== current.originalCall));
        setActiveCall(prev => prev === current.originalCall ? null : prev);
        setIsMuted(false);
      }, 1200);
    } catch (error) {
      clearConsultation({ restoreAudio: true, disconnectCall: false });
      toast.error(`No se pudo completar la transferencia: ${error.message}`);
    }
  };

  const cancelWarmConsultation = () => {
    clearConsultation();
    toast.info('Consulta cancelada; sigues conectado con el cliente');
  };

  // Manejar selección de agente
  const handleAgentSelection = (agente, warmTransfer = false) => {
    setShowAgentSelectionModal(false);
    if (warmTransfer) {
      startWarmConsultation(agente);
    } else {
      ejecutarTransferencia(agente.id, agente.nombre);
    }
  };

  // Cancelar selección de agente
  const handleCancelAgentSelection = () => {
    setShowAgentSelectionModal(false);
    setAvailableAgents([]);
  };

  // Ejecutar la transferencia (solo cold transfer)
  const ejecutarTransferencia = async (agenteId, agenteNombre) => {
    console.log('🔄 INICIANDO TRANSFERENCIA...');
    console.log('📞 Call SID:', activeCall.parameters.CallSid);
    console.log('👤 Target Agent ID:', agenteId);
    console.log('👤 Target Agent Name:', agenteNombre);
    
    // Verificar estado del agente antes de transferir
    const estadoAgente = await verificarEstadoAgente(agenteId);
    console.log('🔍 Estado del agente destino:', estadoAgente);
    
    if (!estadoAgente.conectado) {
      toast.error(`${agenteNombre} no está disponible para recibir la transferencia`);
      return;
    }
    
    // 🔧 FIX: Verificar si el agente está en llamada
    if (estadoAgente.agente && estadoAgente.agente.estado === 'en_llamada') {
      toast.error(`${agenteNombre} está en otra llamada y no puede recibir la transferencia`);
      return;
    }
    
    // COLD TRANSFER - Transferir directamente
    try {
      const authToken = localStorage.getItem('authToken');
      const transferData = {
        callSid: activeCall.parameters.CallSid,
        targetAgentId: agenteId,
        warmTransfer: false // Cold transfer
      };
      
      console.log('📋 Datos de transferencia:', transferData);
      
      const response = await fetch(`${API_BASE_URL}/api/twilio/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      });

      console.log('📡 Respuesta del servidor:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      if (response.ok) {
        const responseText = await response.text();
        console.log('✅ Transferencia exitosa:', responseText);
        toast.success(`Llamada transferida a ${agenteNombre}`);
        
        // Después de transferencia fría exitosa, el agente debe salir de la llamada
        console.log('🔄 Programando limpieza de estado después de transferencia fría...');
        
        // Dar un pequeño delay para que Twilio procese la transferencia
        setTimeout(() => {
          console.log('🧹 Limpiando estado local después de transferencia fría...');
          setActiveCall(null);
          setActiveCalls([]);
          setIsMuted(false);
          console.log('✅ Estado local limpiado - Agente ya no participa en la llamada');
        }, 2000); // 2 segundos de delay
        
        // Limpiar inmediatamente el modal de selección de agentes
        setAvailableAgents([]);
      } else {
        const errorText = await response.text();
        console.error('❌ Error en transferencia:', errorText);
        throw new Error(`Error en la transferencia: ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error ejecutando transferencia:', error);
      toast.error('Error al transferir la llamada');
    }
  };

  // Función para forzar el estado ready manualmente (para debugging)
  const forceDeviceReady = () => {
    console.log('🔧 FORZANDO DEVICE READY MANUALMENTE...');
    if (deviceRef.current && token) {
      console.log('✅ Activando dispositivo existente...');
      isReadyRef.current = true;
      setIsDeviceReady(true);
      setDevice(deviceRef.current);
      toast.success('Dispositivo activado manualmente');
      return true;
    } else {
      console.error('❌ No hay dispositivo o token disponible');
      toast.error('No se puede activar: falta dispositivo o token');
      return false;
    }
  };

  // Función para verificar la configuración del backend
  const checkBackendConfig = async () => {
    console.log('🔍 VERIFICANDO CONFIGURACIÓN DEL BACKEND...');
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No hay token de autenticación');
      }

      // Intentar una llamada de prueba para ver el error específico
      const headers = { 
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Bearer ${authToken}`
      };
      
      const body = new URLSearchParams({ 
        to: '+1234567890', // Número de prueba
        from: '+19787979243',
        test: 'true' // Indicar que es una prueba
      });

      console.log('📡 Enviando solicitud de prueba al backend...');
      const response = await fetch(`${API_BASE_URL}/api/twilio/voice/outgoing`, {
        method: 'POST',
        headers,
        body
      });

      const responseText = await response.text();
      console.log('📋 Respuesta del backend:', {
        status: response.status,
        ok: response.ok,
        body: responseText
      });

      if (!response.ok) {
        try {
          const errorObj = JSON.parse(responseText);
          console.error('❌ Error parseado:', errorObj);
          if (errorObj.detail && errorObj.detail.includes('Invalid application sid')) {
            console.error('🚨 PROBLEMA DETECTADO: Invalid application sid');
            console.error('💡 El backend no tiene configurado correctamente el Application SID de Twilio');
          }
        } catch (e) {
          console.error('Error al parsear respuesta:', e);
        }
      }

      return {
        success: response.ok,
        status: response.status,
        message: responseText
      };
    } catch (error) {
      console.error('❌ Error verificando backend:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Auto-inicializar Twilio cuando el usuario se autentica
  useEffect(() => {
    const initializeTwilioDevice = async () => {
      // 🚫 No inicializar Twilio para operarios
      if (!userHasTwilioAccess) {
        console.log('[TwilioProvider] Usuario sin acceso a Twilio (operario), no se inicializará');
        return;
      }
      
      // Solo inicializar si tenemos usuario, no hemos intentado antes, no estamos inicializando y no tenemos dispositivo
      if (user && user.usuarioId && !initializationAttempted.current && !isInitializing && !device) {
        initializationAttempted.current = true;
        setIsInitializing(true);
        
        try {
          console.log('[TwilioProvider] Auto-inicializando Twilio para usuario:', user.usuarioId);
          
          // Usar username como identity para Twilio
          const identity = user?.username ? user.username : `user_${user.usuarioId}`;
          console.log('[TwilioProvider] Identity:', identity);
          
          // Obtener el token del backend
          const token = await getAccessToken(identity);
          console.log('[TwilioProvider] Token obtenido:', !!token);
          
          if (token) {
            await initializeDevice(token);
            console.log('[TwilioProvider] Dispositivo inicializado correctamente');
          } else {
            console.error('[TwilioProvider] No se pudo obtener el token de Twilio');
          }
        } catch (error) {
          console.error('[TwilioProvider] Error al inicializar Twilio:', error);
        } finally {
          setIsInitializing(false);
        }
      }
    };

    initializeTwilioDevice();
  }, [user, device, getAccessToken, initializeDevice, isInitializing, userHasTwilioAccess]);

  // 🔧 FIX: Notificar desconexión cuando el usuario cierra la pestaña/navegador
  useEffect(() => {
    if (!user?.username || !isDeviceReady) return;

    const handleBeforeUnload = () => {
      // Usar sendBeacon para garantizar que se envíe incluso al cerrar
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        const url = `${API_BASE_URL}/api/twilio/disconnect`;
        const body = `identity=${encodeURIComponent(user.username)}`;
        navigator.sendBeacon(url, new Blob([body], { type: 'application/x-www-form-urlencoded' }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user, isDeviceReady]);

  // Limpiar recursos al desmontar
  useEffect(() => {
    return () => {
      // Notificar desconexión al backend
      if (user && user.username) {
        notificarDesconexion(user.username);
      }
      
      // Limpiar timer de refresh de token
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
      
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }
    };
  }, [user]);

  // Notificar desconexión al backend
  const notificarDesconexion = async (username) => {
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/api/twilio/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: `identity=${encodeURIComponent(username)}`
      });
      console.log('🔴 Desconexión notificada para:', username);
    } catch (error) {
      console.error('Error notificando desconexión:', error);
    }
  };

  // Heartbeat para mantener la conexión activa Y renovar token proactivamente
  useEffect(() => {
    let heartbeatInterval;
    
    if (user && user.username && isDeviceReady) {
      // Enviar heartbeat cada 5 minutos Y renovar token en el dispositivo
      heartbeatInterval = setInterval(async () => {
        try {
          const authToken = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/api/twilio/token?identity=${user.username}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (response.ok) {
            const newTwilioToken = await response.text();
            // 🔄 CRITICAL FIX: Actualizar el token en el dispositivo para evitar desconexión
            if (deviceRef.current && newTwilioToken) {
              try {
                deviceRef.current.updateToken(newTwilioToken);
                console.log('💓 Heartbeat + token renovado para:', user.username);
              } catch (updateError) {
                console.warn('⚠️ Error actualizando token en dispositivo:', updateError);
              }
            } else {
              console.log('💓 Heartbeat enviado (sin dispositivo para actualizar):', user.username);
            }
          } else {
            console.error('❌ Heartbeat falló:', response.status);
          }
        } catch (error) {
          console.error('Error enviando heartbeat:', error);
        }
      }, 5 * 60 * 1000); // 5 minutos
    }

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [user, isDeviceReady]);

  const value = {
    // Estados básicos (compatibilidad)
    device,
    isDeviceReady,
    isInitializing,
    incomingCall,
    activeCall,
    isMuted,
    token,
    
    // ✨ NUEVOS: Estados para múltiples llamadas
    incomingCalls,
    activeCalls,
    consultation,
    hasMultipleIncoming: incomingCalls.length > 1,
    hasMultipleActive: activeCalls.length > 1,
    totalIncomingCalls: incomingCalls.length,
    totalActiveCalls: activeCalls.length,
    isInitiatingOutgoingCall, // ✨ NUEVO: Estado para llamadas salientes
    
    // ✨ Estados para transferencia de llamadas
    showAgentSelectionModal,
    availableAgents,
    allAgents, // 📋 Todos los agentes para lookup de nombres
    handleAgentSelection,
    handleCancelAgentSelection,
    completeWarmTransfer,
    cancelWarmConsultation,
    
    // Funciones básicas
    getAccessToken,
    initializeDevice,
    makeCall,
    toggleMute,
    transferCall,
    obtenerAgentesDisponibles,
    forceDeviceReady,
    checkBackendConfig,
    checkAgentStats,
    verificarEstadoAgente,
    
    // Funciones de compatibilidad (primera llamada)
    acceptCall,
    rejectCall,
    hangUp,
    
    // ✨ NUEVAS: Funciones para múltiples llamadas
    acceptCallById,
    rejectCallById,
    hangUpById,
    hangUpAll
  };

  return (
    <TwilioContext.Provider value={value}>
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilio = () => {
  const context = useContext(TwilioContext);
  if (!context) {
    throw new Error('useTwilio debe ser usado dentro de un TwilioProvider');
  }
  return context;
};